import { NO_CASH_VALUE_DISCLOSURE, NO_CASH_VALUE_DISCLOSURE_TL } from '@tira/protocol';
import type { Phrase } from './copy.js';

/** The settle, the verifier, the age gate and the private room. */
export const RESULTS = {
  /*
   * The one disclosure, in both languages, still owned by the protocol — it is
   * what the store questionnaires are answered against, so it is referenced
   * here rather than retyped.
   */
  'results.disclosure': { tl: NO_CASH_VALUE_DISCLOSURE_TL, en: NO_CASH_VALUE_DISCLOSURE },
  'results.ai': { tl: 'AI', en: 'AI' },

  /* ── The settle ───────────────────────────────────────────────────────── */
  'results.title': { tl: 'Resulta', en: 'Results' },
  'results.empty.title': { tl: 'Wala pang resibo dito.', en: 'No receipt here yet.' },
  'results.empty.body': {
    tl: 'Matapos ang isang laro, lalabas dito ang buong hatian — pati ang tong.',
    en: 'After a game the whole split shows up here — rake and all.',
  },
  'results.back': { tl: 'Balik sa compound', en: 'Back to the compound' },

  'results.verdict.todas': { tl: 'Todas — sa iyo lahat.', en: 'Clean sweep — you took it all.' },
  'results.verdict.over': { tl: 'Tapos na ang laro.', en: 'The game is over.' },
  'results.verdict.win': { tl: 'Panalo ka.', en: 'You won.' },
  'results.verdict.loss': {
    tl: 'Talo ngayon. Bawi sa susunod.',
    en: 'Lost this one. Get it back next time.',
  },
  'results.verdict.draw': { tl: 'Tabla — walang lumamang.', en: 'Draw — nobody came out ahead.' },

  'results.reason.win': { tl: 'May nanalo sa laro', en: 'Somebody won the game' },
  'results.reason.draw': { tl: 'Tabla', en: 'Draw' },
  'results.reason.timeout': { tl: 'Naubusan ng oras ang isa', en: 'Somebody ran out of time' },
  'results.reason.forfeit': { tl: 'May sumuko', en: 'Somebody gave up' },
  'results.reason.abandon': { tl: 'May umalis sa mesa', en: 'Somebody left the table' },

  'results.todas.you': {
    tl: 'Nilinis mo ang mesa. Buong pot, sa iyo.',
    en: 'You swept the table. The whole pot is yours.',
  },
  'results.todas.them': { tl: 'Nilinis ni {who} ang mesa.', en: '{who} swept the table.' },

  'results.rematch.watching': { tl: 'Nanood ka lang', en: 'You were watching' },
  'results.rematch.go': { tl: 'Bawi ako', en: 'Run it back' },
  'results.leave.table': { tl: 'Umalis sa mesa', en: 'Leave the table' },
  'results.fairlink': { tl: 'Suriin ang deal (Fair Play)', en: 'Check the deal (Fair Play)' },
  'results.note.ready': {
    tl: 'Handa na ang {ready} sa {needed}. Naghihintay pa sa iba.',
    en: '{ready} of {needed} ready. Waiting on the rest.',
  },
  'results.note.agree': {
    tl: 'Kapag pumayag lahat, uulitin agad ang laro sa parehong buy-in.',
    en: 'If everyone agrees the game runs again at the same buy-in.',
  },
  'results.note.closed': {
    tl: 'Sarado na ang mesa. Bumalik sa compound para maghanap ng bago.',
    en: 'This table is closed. Head back to the compound to find a new one.',
  },

  'results.slip.head': { tl: 'Resibo ng mesa', en: 'Table receipt' },
  'results.slip.pot': { tl: 'Pot', en: 'Pot' },
  'results.slip.tong': { tl: 'Tong ng bahay', en: 'House rake' },
  'results.slip.tong.why': { tl: '({pct} ng pot)', en: '({pct} of the pot)' },
  'results.slip.net': { tl: 'Hinati sa mesa', en: 'Split at the table' },
  'results.slip.commit': {
    tl: 'Fairness commit ({algo}): {commit}…',
    en: 'Fairness commit ({algo}): {commit}…',
  },

  /* ── Fair play ────────────────────────────────────────────────────────── */
  'results.fair.title': { tl: 'Fair play', en: 'Fair play' },
  'results.fair.sub': { tl: 'Match {id}', en: 'Match {id}' },
  'results.fair.head': {
    tl: 'Paano mo malalamang hindi luto ang deal',
    en: 'How you know the deal is not rigged',
  },
  'results.fair.step1': {
    tl: 'Bago mag-deal, kinukuha ng server ang random na seed at inilalabas ang commit — ang SHA-256 nito. Nakapost na ’yan bago pa may tumira, kaya hindi na ’yan mababago pagkatapos.',
    en: 'Before the deal the server picks a random seed and publishes the commit — its SHA-256. That is posted before anybody moves, so it cannot be changed after.',
  },
  'results.fair.step2': {
    tl: 'Pagkatapos ng laro, inilalabas ang mismong seed. Doon galing ang buong deal: parehong seed, parehong baraha o parehong posisyon, palagi.',
    en: 'After the game the seed itself is published. The whole deal came out of it: same seed, same cards or same positions, every time.',
  },
  'results.fair.step3': {
    tl: 'I-hash mo mismo ang seed dito sa browser mo. Kung pareho ang lumabas sa commit, ibig sabihin hindi pinalitan ang deal matapos mong makita ang unang tira.',
    en: 'Hash the seed yourself, right here in your browser. If it comes out the same as the commit, the deal was not swapped after you saw the first move.',
  },
  'results.fair.error.title': {
    tl: 'Hindi ma-load ang datos ng laro.',
    en: 'Could not load this game’s data.',
  },
  'results.fair.error.body': {
    tl: 'Suriin ang koneksyon mo, tapos balikan ang screen na ito mula sa resibo.',
    en: 'Check your connection, then come back to this screen from the receipt.',
  },
  'results.fair.loading': {
    tl: 'Kinukuha ang commit at seed…',
    en: 'Fetching the commit and the seed…',
  },
  'results.fair.commit': { tl: 'Commit ({algo})', en: 'Commit ({algo})' },
  'results.fair.seed': { tl: 'Seed', en: 'Seed' },
  'results.fair.seed.pending': {
    tl: 'Hindi pa inilalabas — lalabas ito pagkatapos ng laro.',
    en: 'Not published yet — it appears after the game.',
  },
  'results.fair.check': { tl: 'Suriin sa browser ko', en: 'Check it in my browser' },
  'results.fair.serverfail': { tl: 'Bagsak sa server check', en: 'Failed the server check' },
  'results.fair.computed': {
    tl: 'SHA-256 ng seed, galing sa browser mo',
    en: 'SHA-256 of the seed, from your browser',
  },
  'results.fair.match': {
    tl: 'Tugma. Ang seed na ito ang gumawa ng commit na inilabas bago maglaro — hindi ginalaw ang deal.',
    en: 'Match. This seed made the commit published before play — the deal was never touched.',
  },
  'results.fair.mismatch': {
    tl: 'Hindi tugma. Kunan mo ng screenshot ang dalawang string dito at ipadala sa support — kami ang may problema, hindi ikaw.',
    en: 'No match. Screenshot both strings here and send them to support — the problem is ours, not yours.',
  },
  'results.fair.nohash': {
    tl: 'Hindi kayang mag-hash ng browser na ito. Buksan sa https para gumana.',
    en: 'This browser cannot hash. Open it over https to make it work.',
  },

  /* ── Age gate (design spec §13 — the wording is the attestation) ───────── */
  'results.age.title': { tl: 'Pang-18 pataas lang ’to.', en: 'This one’s 18 and over only.' },
  'results.age.body': {
    tl: 'Simulated chips lang ang laro dito — walang cash value, walang cash out.',
    en: 'Simulated chips only here — no cash value, no cash out.',
  },
  'results.age.accept': { tl: 'Tuloy — 18+ ako', en: 'Continue — I’m 18+' },
  'results.age.exit': { tl: 'Labas muna ako', en: 'I’ll step out' },
  'results.age.left.title': { tl: 'Sige, sa susunod na lang.', en: 'Sure, next time then.' },
  'results.age.left.body': {
    tl: 'Isara mo na ang app. Pang-18 pataas lang ang laro dito, kaya wala kang mabubuksan sa gilid na ito.',
    en: 'Go ahead and close the app. Play here is 18 and over only, so there is nothing to open on this side.',
  },
  'results.age.left.back': {
    tl: 'Kung 18+ ka naman, buksan ulit ang app at pindutin ang “Tuloy — 18+ ako”.',
    en: 'If you are 18+, open the app again and tap “Continue — I’m 18+”.',
  },
  'results.age.error': {
    tl: 'Hindi natanggap ang pagkumpirma: {error}. Subukan ulit.',
    en: 'Your confirmation did not go through: {error}. Try again.',
  },

  /* ── Kubo ─────────────────────────────────────────────────────────────── */
  /* Tier names and the word for a private room follow the lobby's English. */
  'results.stake.barya': { tl: 'Barya', en: 'Low stakes' },
  'results.stake.bente': { tl: 'Bente', en: 'Mid stakes' },
  'results.stake.datu': { tl: 'Datu', en: 'High stakes' },

  'results.kubo.title': { tl: 'Kubo', en: 'Private room' },
  'results.kubo.sub': {
    tl: 'Sarilinang mesa para sa barkada',
    en: 'A private table for your crew',
  },
  'results.kubo.in.head': { tl: 'Nasa kubo ka', en: 'You’re in a private room' },
  'results.kubo.in.line': { tl: '{game} · {tier} · buy-in {n}', en: '{game} · {tier} · buy-in {n}' },
  'results.kubo.code.label': { tl: 'Code ng kubo', en: 'Room code' },
  'results.kubo.code.copy': { tl: 'Kopyahin ang code', en: 'Copy the code' },
  'results.kubo.seat.empty': {
    tl: 'Bakante pa — ipadala mo ang code.',
    en: 'Still open — send the code.',
  },
  'results.kubo.host': { tl: 'Host', en: 'Host' },
  'results.kubo.note.host': {
    tl: 'Ikaw ang host: ikaw ang pumili ng laro at ng stake, at ikaw ang huling aalis. Wala pang kick sa build na ito.',
    en: 'You’re the host: you picked the game and the stake, and you leave last. No kick in this build yet.',
  },
  'results.kubo.note.guest': {
    tl: 'Ang host ang may hawak ng laro at ng stake dito.',
    en: 'The host holds the game and the stake here.',
  },
  'results.kubo.fill': { tl: 'Umpisa na — {n} AI', en: 'Start now — {n} AI' },
  'results.kubo.leave': { tl: 'Umalis sa kubo', en: 'Leave the room' },

  'results.kubo.make.head': { tl: 'Gumawa ng kubo', en: 'Open a private room' },
  'results.kubo.make.sub': { tl: 'Ikaw ang host.', en: 'You’re the host.' },
  'results.kubo.field.game': { tl: 'Laro', en: 'Game' },
  'results.kubo.game.soon': { tl: '{game} · malapit na', en: '{game} · coming soon' },
  'results.kubo.lobby.down': {
    tl: 'Kailangan ng internet para makagawa ng kubo. Suriin ang koneksyon, tapos buksan ulit ang screen na ’to.',
    en: 'You need a connection to open a room. Check it, then open this screen again.',
  },
  'results.kubo.lobby.loading': {
    tl: 'Tinitingnan kung anong laro ang bukas…',
    en: 'Checking which games are open…',
  },
  'results.kubo.field.stake': { tl: 'Stake', en: 'Stake' },
  'results.kubo.why.q': {
    tl: 'Bakit walang Datu sa kubo?',
    en: 'Why no high stakes in a private room?',
  },
  'results.kubo.why.body': {
    tl: 'Sa sarilinang mesa, madaling magpatalo nang sadya para lumipat ang chips. Kaya Barya at Bente lang dito, at public matchmaking lang ang Datu. Wala rin talagang paraan sa app na ito para maglipat ng chips sa iba.',
    en: 'At a private table it is easy to lose on purpose so chips move across. So low and mid stakes only here, and high stakes is public matchmaking only. There is no way in this app to move chips to somebody else anyway.',
  },
  'results.kubo.field.buyin': { tl: 'Buy-in ({band})', en: 'Buy-in ({band})' },
  'results.kubo.perseat': {
    tl: '{n} chips kada upuan. Nasa pitaka mo: {bal}.',
    en: '{n} chips per seat. In your wallet: {bal}.',
  },
  'results.kubo.short': {
    tl: 'Kulang ng {n} ang pitaka mo para sa buy-in na ’to. Hilahin pababa ang buy-in, o kumuha muna ng chips.',
    en: 'Your wallet is {n} short for this buy-in. Drag the buy-in down, or get more chips first.',
  },
  'results.kubo.create': { tl: 'Gawin ang kubo', en: 'Open the room' },

  'results.kubo.join.head': { tl: 'Sumali sa kubo', en: 'Join a room' },
  'results.kubo.join.sub': {
    tl: 'Kailangan mo ng anim na karakter na code.',
    en: 'You need a six-character code.',
  },
  'results.kubo.field.code': { tl: 'Code', en: 'Code' },
  'results.kubo.join': { tl: 'Sumali', en: 'Join' },
  'results.kubo.join.note': {
    tl: 'Walang listahan ng kubo — code lang ang pasukan. Kung mali ang code, sasabihin agad namin.',
    en: 'No room list — the code is the only way in. If the code is wrong, we say so right away.',
  },

  'results.kubo.invite.title': { tl: 'Ipadala ang code', en: 'Send the code' },
  'results.kubo.invite.body': {
    tl: 'Bukas na ang kubo mo sa {game}, {tier}, buy-in {n} chips. Hintayin ang barkada — pagdating nila, uupo agad. Kung walang dumating, AI ang uupo para makalaro ka pa rin.',
    en: 'Your room is open at {game}, {tier}, buy-in {n} chips. Wait for your crew — when they arrive they sit right down. If nobody comes, AI sits so you can still play.',
  },
  'results.kubo.invite.done': { tl: 'Sige, tapos na', en: 'Okay, done' },

  'results.kubo.err.create': {
    tl: 'Hindi nagawa ang kubo: {error}. Subukan ulit.',
    en: 'Could not open the room: {error}. Try again.',
  },
  'results.kubo.err.codelen': {
    tl: 'Anim na karakter ang code ng kubo. Tingnan ulit ang pinadala sa’yo.',
    en: 'A room code is six characters. Check the one you were sent.',
  },
  'results.kubo.ok.joined': {
    tl: 'Nasa kubo ka na. Hintayin ang iba.',
    en: 'You’re in the room. Wait for the others.',
  },
  'results.kubo.err.join': {
    tl: 'Hindi ka nakapasok: {error}. Tingnan ang code o hingi ng bago.',
    en: 'You could not get in: {error}. Check the code or ask for a new one.',
  },
  'results.kubo.ok.copied': {
    tl: 'Nakopya ang code. I-paste mo sa barkada.',
    en: 'Code copied. Paste it to your crew.',
  },
  'results.kubo.err.copy': {
    tl: 'Hindi makopya. I-type mo na lang: {code}',
    en: 'Could not copy. Just type it: {code}',
  },
} as const satisfies Record<string, Phrase>;
